/**
 * Credentials API
 *
 * RTK Query endpoints for domain exams and user credentials.
 */

import { api } from '../api';

// ============================================
// TYPES
// ============================================

export type DomainKey =
  | 'real_estate_cold_caller'
  | 'real_estate_realtor'
  | 'real_estate_flipper'
  | 'tech_sales_sdr'
  | 'tech_sales_ae'
  | 'investing_public_markets'
  | 'ecommerce_brand_owner'
  | 'coach_executive'
  | 'coach_life'
  | 'coach_health_fitness';

export type DomainCategory =
  | 'real_estate'
  | 'tech_sales'
  | 'investing'
  | 'ecommerce'
  | 'coaching';

export type DomainExamStatus = 'not_started' | 'in_progress' | 'passed' | 'failed';

export interface DomainExamDefinition {
  key: DomainKey;
  label: string;
  description: string;
  category: DomainCategory;
  recommendedExperience: string;
  passingScoreOverall: number;
  minSessions: number;
  minConversationLengthSeconds: number;
  relatedScenarioKeys: string[];
  icon?: string;
}

export interface UserDomainCredential {
  domainKey: DomainKey;
  label: string;
  category: string;
  status: DomainExamStatus;
  overallScore: number;
  lastCompletedAt?: string;
  attemptsCount: number;
  passingScore: number;
}

/** Another user's passed credential (public allow-list): no attempt count. */
export type PublicDomainCredential = Omit<UserDomainCredential, 'attemptsCount'>;

export interface ExamStatusResponse {
  userId: string;
  domainKey: DomainKey;
  attemptId?: string;
  status: DomainExamStatus;
  overallScore?: number;
  startedAt?: string;
  completedAt?: string;
  attemptsCountForDomain: number;
  passingScore: number;
  examDefinition?: DomainExamDefinition;
}

export interface ExamProgressResponse {
  attemptId: string;
  sessionsCompleted: number;
  minSessionsRequired: number;
  totalConversationSeconds: number;
  minConversationSecondsRequired: number;
  currentAvgScore: number;
  passingScore: number;
  isReadyToComplete: boolean;
}

export interface StartExamResponse {
  message: string;
  attemptId: string;
  examDefinition: DomainExamDefinition;
}

// The server reads score and duration from its own scored session row; a
// request carrying either is refused (400 CLIENT_SCORE_REJECTED).
export interface LinkSessionRequest {
  sessionId: string;
}

export interface LinkSessionResponse {
  message: string;
  progress: ExamProgressResponse;
}

// No score: the server computes it. Sending one is refused (400).
export interface CompleteExamRequest {
  attemptId: string;
}

export interface ExamAttemptResult {
  attemptId: string;
  userId: string;
  domainKey: DomainKey;
  status: DomainExamStatus;
  overallScore: number;
  startedAt: string;
  completedAt: string;
  sessionsCompleted: number;
  totalDurationSeconds: number;
  passingScore: number;
}

export interface CompleteExamResponse {
  message: string;
  result: ExamAttemptResult;
  /** True when the attempt was already completed; the stored result is returned unchanged. */
  alreadyCompleted: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/** GET /verify/:hash `data`, as CredentialLicenseService.getPublicCredentialByHash returns it. */
export interface ServerCredentialVerification {
  status: 'verified' | 'revoked' | 'expired' | 'not_found';
  userDisplayName?: string;
  domainKey?: string;
  domainLabel?: string;
  category?: string;
  authorityLevelAtIssue?: string;
  issuedAt?: string;
  expiresAt?: string | null;
}

/**
 * The server's verification → the page's view. The server answers with a
 * `status`; the page reads `isValid` + `credential`. Handing the raw body
 * through made every VALID credential render as "Credential Not Valid".
 */
export function toCredentialVerification(data: unknown): PublicCredentialVerification {
  const d = (data ?? {}) as Partial<ServerCredentialVerification>;
  switch (d.status) {
    case 'verified':
      if (!d.domainKey || !d.domainLabel || !d.issuedAt) {
        return { isValid: false, reason: 'This credential could not be verified.' };
      }
      return {
        isValid: true,
        credential: {
          domainKey: d.domainKey,
          domainLabel: d.domainLabel,
          category: d.category ?? '',
          issuedAt: d.issuedAt,
          expiresAt: d.expiresAt ?? null,
          isActive: true,
          // The server names the holder by the public-name rule (never the legal name).
          holderName: d.userDisplayName || 'BeHeard member',
          authorityLevel: d.authorityLevelAtIssue ?? '',
        },
      };
    case 'revoked':
      return {
        isValid: false,
        reason: `This ${d.domainLabel ? `${d.domainLabel} ` : ''}credential has been revoked and is no longer valid.`,
      };
    case 'expired': {
      const at = d.expiresAt ? new Date(d.expiresAt) : null;
      return {
        isValid: false,
        reason:
          at && !Number.isNaN(at.getTime())
            ? `This ${d.domainLabel ? `${d.domainLabel} ` : ''}credential expired on ${at.toLocaleDateString()}.`
            : 'This credential has expired.',
      };
    }
    case 'not_found':
      return { isValid: false, reason: 'No credential matches this verification link.' };
    default:
      return { isValid: false, reason: 'This credential could not be verified.' };
  }
}

export interface PublicCredentialVerification {
  isValid: boolean;
  credential?: {
    domainKey: string;
    domainLabel: string;
    category: string;
    issuedAt: string;
    expiresAt: string | null;
    isActive: boolean;
    holderName: string;
    authorityLevel: string;
  };
  reason?: string;
}

export interface VerifiedCredential {
  id: string;
  userId: string;
  domainKey: DomainKey;
  domainLabel: string;
  category: string;
  authorityLevelAtIssue: string;
  issuedAt: string;
  expiresAt: string | null;
  isActive: boolean;
  verificationHash: string;
}

// ============================================
// API ENDPOINTS
// ============================================

export const credentialsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================
    // PUBLIC ENDPOINTS
    // ============================================

    // Get all exam definitions
    getAllExams: builder.query<DomainExamDefinition[], void>({
      query: () => '/credentials/exams',
      transformResponse: (response: ApiResponse<DomainExamDefinition[]>) => response.data,
    }),

    // Get a specific exam definition
    getExamDefinition: builder.query<DomainExamDefinition, DomainKey>({
      query: (domainKey) => `/credentials/exams/${domainKey}`,
      transformResponse: (response: ApiResponse<DomainExamDefinition>) => response.data,
    }),

    // Get public credentials for a user
    getUserCredentials: builder.query<PublicDomainCredential[], string>({
      query: (userId) => `/credentials/users/${userId}`,
      transformResponse: (response: ApiResponse<PublicDomainCredential[]>) => response.data,
    }),

    // Verify a credential by hash (public endpoint)
    verifyCredential: builder.query<PublicCredentialVerification, string>({
      query: (hash) => `/verify/${hash}`,
      transformResponse: (response: ApiResponse<ServerCredentialVerification>) =>
        toCredentialVerification(response?.data),
    }),

    // ============================================
    // AUTHENTICATED ENDPOINTS
    // ============================================

    // Get my credentials
    getMyCredentials: builder.query<UserDomainCredential[], void>({
      query: () => '/credentials/me',
      transformResponse: (response: ApiResponse<UserDomainCredential[]>) => response.data,
      providesTags: ['User'],
    }),

    // Get my verified (licensed) credentials
    getMyVerifiedCredentials: builder.query<VerifiedCredential[], void>({
      query: () => '/credentials/me/verified',
      transformResponse: (response: ApiResponse<VerifiedCredential[]>) => response.data,
      providesTags: ['User'],
    }),

    // Get my active exam progress
    getMyExamProgress: builder.query<ExamProgressResponse, DomainKey>({
      query: (domainKey) => `/credentials/me/exam/${domainKey}/progress`,
      transformResponse: (response: ApiResponse<ExamProgressResponse>) => response.data,
      providesTags: ['User'],
    }),

    // Start an exam
    startExam: builder.mutation<StartExamResponse, DomainKey>({
      query: (domainKey) => ({
        url: `/credentials/exams/${domainKey}/start`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<StartExamResponse>) => response.data,
      invalidatesTags: ['User'],
    }),

    // Link a session to an active exam
    linkSessionToExam: builder.mutation<
      LinkSessionResponse,
      { domainKey: DomainKey } & LinkSessionRequest
    >({
      query: ({ domainKey, ...body }) => ({
        url: `/credentials/exams/${domainKey}/session`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<LinkSessionResponse>) => response.data,
      invalidatesTags: ['User'],
    }),

    // Complete an exam
    completeExam: builder.mutation<
      CompleteExamResponse,
      { domainKey: DomainKey } & CompleteExamRequest
    >({
      query: ({ domainKey, ...body }) => ({
        url: `/credentials/exams/${domainKey}/complete`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CompleteExamResponse>) => response.data,
      invalidatesTags: ['User'],
    }),
  }),
  overrideExisting: false,
});

export const {
  // Query hooks
  useGetAllExamsQuery,
  useGetExamDefinitionQuery,
  useGetUserCredentialsQuery,
  useVerifyCredentialQuery,
  useGetMyCredentialsQuery,
  useGetMyVerifiedCredentialsQuery,
  useGetMyExamProgressQuery,
  // Mutation hooks
  useStartExamMutation,
  useLinkSessionToExamMutation,
  useCompleteExamMutation,
} = credentialsApi;
